import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Store, MapPin, Wrench, Calendar, ArrowRight, Mail, Zap, Users, Building2, Clock, Award, Phone, Target, HardHat, Plug, Droplets, Hammer, Paintbrush, Shovel } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { SEO } from "../components/SEO";
import { CountdownTimer } from "../components/home/CountdownTimer";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BusinessCard, BusinessCardFull } from "../components/BusinessCard";
import { MembershipModal, SponsorModal } from "../components/MembershipModals";
import SmartSearch from "../components/SmartSearch";
import TownPicker from "../components/TownPicker";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [businesses, setBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  
  // Modal states
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [initialMembershipTier, setInitialMembershipTier] = useState(null);
  const [foundingSpots, setFoundingSpots] = useState(20);
  const [foundingCounts, setFoundingCounts] = useState({ business: { remaining: 20, max: 20 }, corporate: { remaining: 10, max: 10 }, industrial: { remaining: 2, max: 2 } });
  const [spotlightBiz, setSpotlightBiz] = useState(null);
  const [contractorCount, setContractorCount] = useState(0);

  useEffect(() => {
    fetchBusinesses();
    fetchFoundingSpots();
    fetchSpotlight();
    fetchContractorCount();
    seedData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetches, stable functions

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch (e) {
      // Already seeded or error
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses`, {
        params: { featured_only: true }
      });
      setBusinesses(response.data.slice(0, 6));
    } catch (e) {
      console.error("Error fetching businesses:", e);
    }
  };

  const fetchFoundingSpots = async () => {
    try {
      const response = await axios.get(`${API}/membership/founding-spots`);
      setFoundingCounts(response.data);
      setFoundingSpots(response.data.business?.remaining ?? 20);
    } catch (e) {
      console.error("Error fetching founding spots:", e);
    }
  };

  const fetchSpotlight = async () => {
    try {
      const response = await axios.get(`${API}/businesses/spotlight`);
      setSpotlightBiz(response.data);
    } catch (e) {
      console.error("Error fetching spotlight:", e);
    }
  };

  const fetchContractorCount = async () => {
    try {
      const response = await axios.get(`${API}/businesses`, { params: { category: "Contractors", page_size: 1 } });
      // We get back array, but we need total count. Use the array length as a proxy
      const allRes = await axios.get(`${API}/businesses`, { params: { category: "Contractors", page_size: 500 } });
      setContractorCount(allRes.data.length);
    } catch (e) {
      console.error("Error fetching contractor count:", e);
    }
  };

  const openMembershipModal = (tier = null) => {
    setInitialMembershipTier(tier);
    setShowMembershipModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/directory?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to the newsletter!");
      setEmail("");
    } catch (e) {
      toast.error("Failed to subscribe. Please try again.");
    }
    setSubscribing(false);
  };

  const categories = [
    { name: "Businesses", icon: Store, color: "bg-[#2C4A3B]", link: "/directory?category=Businesses" },
    { name: "Tourism", icon: MapPin, color: "bg-[#2C4A3B]", link: "/directory?category=Tourism" },
    { name: "Contractors", icon: Wrench, color: "bg-[#2C4A3B]", link: "/directory?category=Contractors" },
    { name: "Events", icon: Calendar, color: "bg-[#2C4A3B]", link: "/directory?category=Events" },
    { name: "Marketplace", icon: Target, color: "bg-[#D4A017]", link: "/marketplace" }
  ];

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO 
        title="East Kootenay Business Directory"
        description="Find every business, service, and experience in East Kootenay, BC. Local directory featuring Golden, Invermere, Cranbrook, Fernie, Creston, Kimberley and more. Built here. For here."
        keywords="East Kootenay, Golden, Invermere, Cranbrook, Fernie, Creston, Kimberley, BC business directory, local businesses"
      />
      
      {/* HERO SECTION */}
      <section 
        className="relative min-h-screen flex flex-col bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1626816894318-422017eddfa6?w=1920&q=80')` 
        }}
        data-testid="hero-section"
      >
        <div className="hero-overlay absolute inset-0" />
        <Navbar />
        <TownPicker />
        
        <div className="relative z-10 text-center px-6 pt-36 pb-20 max-w-4xl mx-auto flex-1 flex flex-col items-center justify-center">
          <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            What do you want to do now?
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 font-light animate-fade-in stagger-1">
            Find every business, service, and experience available in the East Kootenays. If you're not here, you should be.
          </p>

          {/* Smart Search Bar */}
          <div className="mb-8 animate-fade-in stagger-2 max-w-2xl mx-auto">
            <SmartSearch variant="hero" />
          </div>

          {/* Category Buttons */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 animate-fade-in stagger-3">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={cat.link}
                className={`category-btn ${cat.color} text-white px-5 py-3 rounded-lg font-medium flex items-center gap-2 border-2 border-white/20 hover:border-white/50`}
                data-testid={`category-btn-${cat.name.toLowerCase()}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="relative z-10 pb-8 flex justify-center animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* QUICK ACCESS TICKER */}
      <div className="bg-[#1A1915] border-y-2 border-[#D4A017]/30 overflow-hidden" data-testid="quick-access-ticker">
        <div className="ticker-track flex whitespace-nowrap py-3">
          {[0, 1].map((dupeIdx) => (
            <div key={dupeIdx} className="ticker-content flex items-center gap-0 shrink-0" aria-hidden={dupeIdx === 1}>
              {[
                { label: "Browse Directory", to: "/directory" },
                { label: "List Your Business", to: "/list-business" },
                { label: "Share a Moment", to: "/blog" },
                { label: "Volunteer", to: "/volunteer" },
                { label: "Marketplace", to: "/marketplace" },
                { label: "Support Us", to: "/donate" },
                { label: "News & Blog", to: "/blog" },
                { label: "Our Story", to: "/about" },
              ].map((item, i) => (
                <Link
                  key={`${dupeIdx}-${i}`}
                  to={item.to}
                  className="inline-flex items-center gap-2 px-6 text-sm font-medium text-white/80 hover:text-[#D4A017] transition-colors"
                >
                  <span className="text-[#D4A017]">/</span>
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* FIND A CONTRACTOR — Primary CTA Section */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#1A1915]" data-testid="contractor-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#D4A017]/15 text-[#D4A017] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              <HardHat className="w-3.5 h-3.5" />
              East Kootenay Contractor Network
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3">
              Find a Contractor
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              {contractorCount > 0 ? `${contractorCount} verified contractors` : "Local contractors"} across the East Kootenay — from Golden to Creston. Find the right trade for your project.
            </p>
          </div>

          {/* Trade Categories Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {[
              { name: "Electrical", icon: Plug, search: "electrician electrical" },
              { name: "Plumbing", icon: Droplets, search: "plumber plumbing" },
              { name: "Carpentry", icon: Hammer, search: "carpenter carpentry framing" },
              { name: "Roofing", icon: HardHat, search: "roofer roofing" },
              { name: "Painting", icon: Paintbrush, search: "painter painting" },
              { name: "Excavation", icon: Shovel, search: "excavation excavator landscaping" },
            ].map((trade) => (
              <Link
                key={trade.name}
                to={`/directory?category=Contractors&search=${encodeURIComponent(trade.search)}`}
                className="group flex flex-col items-center gap-3 bg-white/5 hover:bg-[#D4A017]/10 border-2 border-white/10 hover:border-[#D4A017]/40 rounded-xl p-5 transition-all"
                data-testid={`trade-${trade.name.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#D4A017]/10 group-hover:bg-[#D4A017]/20 flex items-center justify-center transition-colors">
                  <trade.icon className="w-6 h-6 text-[#D4A017]" />
                </div>
                <span className="text-white/80 group-hover:text-white text-sm font-medium transition-colors">{trade.name}</span>
              </Link>
            ))}
          </div>

          {/* CTA Row */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/directory?category=Contractors"
              className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#E8B731] text-[#1A1915] font-bold px-8 py-4 rounded-lg transition-colors"
              data-testid="browse-all-contractors-btn"
            >
              <Wrench className="w-5 h-5" />
              Browse All Contractors
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/list-business"
              className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white/40 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              data-testid="list-contractor-btn"
            >
              List Your Trade
            </Link>
          </div>
        </div>
      </section>

      {/* INDIGENOUS BUSINESS & COMMUNITY PARTNERS */}
      <section className="py-16 md:py-20 px-6 md:px-12 bg-[#F7F6F2]" data-testid="indigenous-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A1915] mb-6">
              Indigenous Business & Community Partners
            </h2>
            <div className="max-w-3xl mx-auto space-y-4 text-[#5C5A52] text-lg leading-relaxed">
              <p>
                The EK Valleys sit within the traditional territory of the Ktunaxa Nation.
              </p>
              <p>
                EkValleys is committed to helping highlight and support Indigenous businesses, entrepreneurs, and community initiatives across the region.
              </p>
              <p>
                This section is intended to provide visibility for Indigenous-owned businesses and organizations so they can more easily connect with residents, visitors, and regional opportunities.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/marketplace?tab=providers&category=Indigenous+Businesses"
              className="inline-flex items-center gap-2 bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              data-testid="browse-indigenous-btn"
            >
              <Store className="w-5 h-5" />
              Browse Indigenous Businesses
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/list-business"
              className="inline-flex items-center gap-2 border-2 border-[#2C4A3B] text-[#2C4A3B] hover:bg-[#2C4A3B] hover:text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              data-testid="list-indigenous-btn"
            >
              List Your Business
            </Link>
          </div>
        </div>
      </section>

      {/* DIRECTORY PREVIEW SECTION */}
      <section className="py-20 md:py-28 px-6 md:px-12" data-testid="directory-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#1A1915] mb-4">
              Discover Local Businesses
            </h2>
            <p className="text-[#5C5A52] text-lg max-w-2xl mx-auto">
              Find every business, service, and experience in East Kootenay.
            </p>
          </div>

          {/* Business Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {businesses.map((business, idx) => (
              <div key={business.id} className={`animate-fade-in stagger-${idx + 1}`}>
                <BusinessCard 
                  business={business} 
                  onClick={() => setSelectedBusiness(business)}
                />
              </div>
            ))}
          </div>

          {/* See All Button */}
          <div className="text-center">
            <Link 
              to="/directory"
              className="inline-flex items-center gap-2 bg-[#2C4A3B] text-white font-semibold px-8 py-4 rounded-lg border-2 border-[#1A1915] btn-shadow"
              data-testid="see-all-businesses-btn"
            >
              See All Businesses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BUSINESS OF THE WEEK SPOTLIGHT */}
      {spotlightBiz && (
        <section className="py-16 md:py-24 px-6 md:px-12 bg-[#1A1915]" data-testid="spotlight-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <Award className="w-7 h-7 text-[#D4A017]" />
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white italic">
                Business of the Week
              </h2>
              <Award className="w-7 h-7 text-[#D4A017]" />
            </div>

            <div className="bg-gradient-to-br from-[#2C4A3B] to-[#1A2F23] border-2 border-[#D4A017]/30 rounded-2xl overflow-hidden">
              <div className="grid md:grid-cols-5 gap-0">
                {/* Left: Gold accent bar */}
                <div className="md:col-span-2 bg-gradient-to-b from-[#D4A017]/20 to-[#D4A017]/5 p-8 md:p-10 flex flex-col justify-center items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#D4A017]/20 border-2 border-[#D4A017] flex items-center justify-center mb-4">
                    <Store className="w-10 h-10 text-[#D4A017]" />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2" data-testid="spotlight-name">
                    {spotlightBiz.name}
                  </h3>
                  {spotlightBiz.membership_tier === "founding" && (
                    <span className="inline-flex items-center gap-1.5 bg-[#14B8A6]/20 text-[#14B8A6] text-xs font-semibold px-3 py-1 rounded-full border border-[#14B8A6]/30 mb-3">
                      <Zap className="w-3 h-3" />
                      Founding Member
                    </span>
                  )}
                  <p className="text-white/50 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {spotlightBiz.town}
                  </p>
                </div>

                {/* Right: Details */}
                <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                  <p className="text-white/80 text-lg leading-relaxed mb-6">
                    {spotlightBiz.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    {spotlightBiz.owner_name && (
                      <div className="flex items-center gap-3 text-white/70">
                        <Users className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
                        <span>Owner: <span className="text-white font-medium">{spotlightBiz.owner_name}</span></span>
                      </div>
                    )}
                    {spotlightBiz.address && (
                      <div className="flex items-center gap-3 text-white/70">
                        <MapPin className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
                        <span>{spotlightBiz.address}</span>
                      </div>
                    )}
                    {spotlightBiz.phone && (
                      <div className="flex items-center gap-3 text-white/70">
                        <Phone className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
                        <a href={`tel:${spotlightBiz.phone}`} className="hover:text-white transition-colors">{spotlightBiz.phone}</a>
                      </div>
                    )}
                    {spotlightBiz.hours && (
                      <div className="flex items-center gap-3 text-white/70">
                        <Clock className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
                        <span>{spotlightBiz.hours}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {spotlightBiz.website && (
                      <a
                        href={spotlightBiz.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#D4A017] text-[#1A1915] font-semibold px-6 py-2.5 rounded-lg hover:bg-[#E8B731] transition-colors"
                        data-testid="spotlight-website-link"
                      >
                        Visit Website
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => setSelectedBusiness(spotlightBiz)}
                      className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-6 py-2.5 rounded-lg hover:border-white/40 transition-colors"
                      data-testid="spotlight-details-btn"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* THE STORY SECTION */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#F7F6F2]" data-testid="story-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-[#D1CFC5]">
                <img 
                  src="https://images.unsplash.com/photo-1607969891567-463aef4c9ddc?w=800&q=80"
                  alt="East Kootenay founder"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#D4A017] rounded-lg -z-10" />
            </div>

            {/* Text */}
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#1A1915] mb-6 italic">
                The Story
              </h2>
              <div className="space-y-4 text-[#5C5A52] text-lg leading-relaxed">
                <p>
                  I didn't come to the East Kootenays because of a business plan. I came because of a girl.
                </p>
                <p>
                  What I found was something special: strong communities, hardworking people, and a question that kept appearing everywhere — at coffee shops, at job sites, between friends trying to make plans.
                </p>
                <p className="font-medium text-[#1A1915] italic">
                  "What do we do now?"
                </p>
                <p>
                  The region didn't lack energy, talent, or generosity. What it lacked was a system that connected everything together.
                </p>
              </div>
              <Link 
                to="/about"
                className="inline-flex items-center gap-2 mt-8 text-[#2C4A3B] font-semibold hover:gap-3 transition-all"
                data-testid="read-full-story-link"
              >
                Read the full story
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CTA SECTION */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#1A2F23]" data-testid="membership-cta-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4 italic">
              Grow with the Valley
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Whether you're listing your business or supporting the community, there's a place for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* SMB Founding Member Card */}
            <div className="bg-gradient-to-br from-[#14B8A6]/20 to-[#1A1915]/40 border-2 border-[#14B8A6]/50 rounded-xl p-6 text-center" data-testid="founding-cta-card">
              <Zap className="w-10 h-10 text-[#14B8A6] mx-auto mb-3" />
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Founding Member</h3>
              <p className="text-[#14B8A6] text-2xl font-bold mb-1">$49<span className="text-sm font-normal text-white/60">/yr</span></p>
              <p className="text-white/60 text-sm mb-3">{foundingCounts.business?.remaining ?? foundingSpots} of {foundingCounts.business?.max ?? 20} spots left</p>
              <div className="bg-[#14B8A6]/10 border border-[#14B8A6]/30 rounded-lg px-3 py-2 mb-4">
                <p className="text-[#14B8A6] text-xs font-semibold uppercase tracking-wide mb-1">20% off — locked in for life</p>
                <p className="text-white/60 text-[11px]">$49/yr or 80% equivalent. Guaranteed.</p>
              </div>
              <Button
                onClick={() => openMembershipModal("founding")}
                className="w-full bg-[#14B8A6] hover:bg-[#0D9488] text-[#1A1915] font-semibold"
                data-testid="claim-founding-spot-btn"
              >
                Claim Your Spot
              </Button>
            </div>

            {/* Corporate Founding Partner Card */}
            <div className="bg-gradient-to-br from-[#6366F1]/20 to-[#1A1915]/40 border-2 border-[#6366F1]/50 rounded-xl p-6 text-center" data-testid="founding-corporate-cta-card">
              <Building2 className="w-10 h-10 text-[#6366F1] mx-auto mb-3" />
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Corporate Partner</h3>
              <p className="text-[#6366F1] text-2xl font-bold mb-1">From $199<span className="text-sm font-normal text-white/60">/yr</span></p>
              <p className="text-white/60 text-sm mb-3">{foundingCounts.corporate?.remaining ?? 10} of {foundingCounts.corporate?.max ?? 10} spots left</p>
              <div className="bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-lg px-3 py-2 mb-4">
                <p className="text-[#6366F1] text-xs font-semibold uppercase tracking-wide mb-1">20% off — locked in for life</p>
                <p className="text-white/60 text-[11px]">80% of yearly rate, locked in. Guaranteed.</p>
              </div>
              <Button
                onClick={() => openMembershipModal("corporate_standard")}
                className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-semibold"
                data-testid="claim-corporate-founding-btn"
              >
                Claim Your Spot
              </Button>
            </div>

            {/* Industrial Founding Partner Card */}
            <div className="bg-gradient-to-br from-[#DC2626]/20 to-[#1A1915]/40 border-2 border-[#DC2626]/50 rounded-xl p-6 text-center" data-testid="founding-industrial-cta-card">
              <Wrench className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
              <h3 className="font-heading text-2xl font-bold text-white mb-2">Industrial Partner</h3>
              <p className="text-[#DC2626] text-2xl font-bold mb-1">From $1,599<span className="text-sm font-normal text-white/60">/yr</span></p>
              <p className="text-white/60 text-sm mb-3">{foundingCounts.industrial?.remaining ?? 2} of {foundingCounts.industrial?.max ?? 2} spots left</p>
              <div className="bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-lg px-3 py-2 mb-4">
                <p className="text-[#DC2626] text-xs font-semibold uppercase tracking-wide mb-1">20% off — locked in for life</p>
                <p className="text-white/60 text-[11px]">80% of yearly rate, locked in. Guaranteed.</p>
              </div>
              <Button
                onClick={() => openMembershipModal("industrial_standard")}
                className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold"
                data-testid="claim-industrial-founding-btn"
              >
                Claim Your Spot
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER SECTION */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#EFEDE5]" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="w-12 h-12 text-[#2C4A3B] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A1915] mb-4">
            The East Kootenay Insider
          </h2>
          <p className="text-[#5C5A52] text-lg mb-8">
            What's happening, who's here, and what to do next. Free. Weekly.
          </p>

          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="your@email.com"
              className="bg-white border-2 border-[#D1CFC5] px-4 py-3 rounded-lg flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              disabled={subscribing}
              className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-6 py-3 rounded-lg border-2 border-[#1A1915] btn-shadow"
              data-testid="newsletter-subscribe-btn"
            >
              {subscribing ? "Subscribing..." : "Subscribe"}
            </Button>
          </form>

          <p className="text-[#5C5A52]/60 text-sm mt-4">
            Also available on{" "}
            <a 
              href="https://ekvalleys.substack.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline hover:text-[#2C4A3B]"
            >
              Substack
            </a>
          </p>
        </div>
      </section>

      <Footer />

      {/* Business Detail Modal */}
      {selectedBusiness && (
        <BusinessCardFull 
          business={selectedBusiness} 
          onClose={() => setSelectedBusiness(null)} 
        />
      )}

      {/* Membership Modal */}
      <MembershipModal 
        isOpen={showMembershipModal} 
        onClose={() => setShowMembershipModal(false)}
        initialTier={initialMembershipTier}
      />

      {/* Sponsor Modal */}
      <SponsorModal
        isOpen={showSponsorModal}
        onClose={() => setShowSponsorModal(false)}
      />
    </div>
  );
};

export default Home;
